import React from 'react';
import { Modal } from '@ami/react/core';
import { Quote } from '../../quotes/quote';
import { withRouter } from 'react-router-dom';
import { NotificationManager } from 'react-notifications';
import QuoteView from './quoteView';
import QuoteViewComponent from './QuoteViewComponent';
import ChangeBranchModal from './ChangeBranchModal';
import ChangePriceZoneModal from './ChangePriceZoneModal';
import CopyQuoteModal from './CopyQuoteModal';
import ReOpenQuoteConfirmationModal from './quote-details/ReOpenQuoteConfirmationModal';
import CustomActivityModal from './quote-details/CustomActivityModal';
import { Spinner } from '@ami/react/core';
import { appConfig } from '@ami/react/core';
import ReSendCreateNotificationModal from './quote-details/ReSendCreateNotificationModal';
import { quoteProductLeadTimeType } from '../../quotes/otherQuoteEntities';
import QuoteCancelModal from '../../quotes/view/QuoteCancelModal';
import QuoteExpireModal from '../../quotes/view/QuoteExpireModal';
import { CancelConfirmationModal } from '@ami/react/core';
import RequestNewLeadTimeModal from './quote-details/RequestNewLeadTimeModal';

const LeadTimeChangesConfirmationModal = ({
  show,
  onShowUpdated,
  onConfirm,
}) => (
  <Modal
    title="Lead Time Changes"
    show={show}
    onShowUpdated={onShowUpdated}
    okTitle="Confirm"
    cancelTitle="Dismiss"
    onOk={onConfirm}
    showCancel={true}
  >
    <p>
      Please notice that some of the lead times for this quote have changed.
    </p>
  </Modal>
);

class QuoteViewPage extends React.Component {
  constructor(props) {
    super(props);
    this.changeBranchModalRef = React.createRef();
    this.changePriceZoneModalRef = React.createRef();
    this.reSendCreateNotificationModalRef = React.createRef();
    this.reOpenQuoteConfirmationModalRef = React.createRef();
    this.customActivityModalRef = React.createRef();
    this.requestNewLeadTimeModalRef = React.createRef();
    this.copyQuoteModalRef = React.createRef();
  }

  state = {
    quote: null,
    showCancelConfirmationModal: false,
    showExpireConfirmationModal: false,
    showLeadTimeChangesConfirmationModal: false,
    showUpdatePartNumbersConfirmationModal: false,
    saving: false,
  };

  async componentDidMount() {
    const quote = await QuoteView.get(this.props.match.params.id);
    const showLeadTimeChangesConfirmationModal =
      appConfig.isDealerApp && quote.leadTimeChangesUnconfirmed;

    this.setState({
      quote,
      showLeadTimeChangesConfirmationModal,
    });
  }

  componentDidUpdate(prevProps) {
    if (prevProps.match.params.id !== this.props.match.params.id)
      this.loadQuote();
  }

  loadQuote = async () => {
    const quote = await QuoteView.get(this.props.match.params.id);
    const showLeadTimeChangesConfirmationModal =
      appConfig.isDealerApp && quote.leadTimeChangesUnconfirmed;

    this.setState({
      quote,
      showLeadTimeChangesConfirmationModal,
    });
  };

  navigateToQuotesList = () => this.props.history.push('/quotes');

  navigateToQuote = (id) => this.props.history.push(`/quotes/view/${id}`);

  cancel = () => {
    const { quote } = this.state;
    NotificationManager.success(`Quote ${quote.number} cancelled`);
    this.navigateToQuotesList();
  };

  expire = async () => {
    const { quote } = this.state;
    NotificationManager.success(`Quote ${quote.number} expired`);
    this.loadQuote();
  };

  confirmLeadTimeChanges = async () => {
    const { quote } = this.state;
    await Quote.confirmLeadTimeChanges(quote.id).then(() => this.loadQuote());
  };

  handleOnUpdatePartNumbers = async () => {
    const { quote } = this.state;
    this.setState({ saving: true });
    await Quote.updatePartNumbers(quote.id)
      .then(() => {
        this.loadQuote();
        NotificationManager.success('Part Numbers updated');
        this.setState({ showUpdatePartNumbersConfirmationModal: false });
      })
      .finally(() => this.setState({ saving: false }));
  };

  render() {
    if (!this.state.quote) {
      return (
        <div className="container text-center">
          <h4 className="mt-5 mb-2">Loading</h4>
          <Spinner show={true} size="fa-5x" />
        </div>
      );
    }
    const {
      quote,
      showCancelConfirmationModal,
      showExpireConfirmationModal,
      showUpdatePartNumbersConfirmationModal,
      saving,
    } = this.state;

    return (
      <div className="container">
        <QuoteViewComponent
          quote={this.state.quote}
          onQuoteUpdated={this.loadQuote}
          onCancel={() => this.setState({ showCancelConfirmationModal: true })}
          onExpire={() => this.setState({ showExpireConfirmationModal: true })}
          onDealerBranchChange={() => this.changeBranchModalRef.current.show()}
          onPriceZoneChange={() => this.changePriceZoneModalRef.current.show()}
          onReSendCreateNotification={() =>
            this.reSendCreateNotificationModalRef.current.show()
          }
          onAddCustomActivity={() => this.customActivityModalRef.current.show()}
          onRequestNewLeadTime={() =>
            this.requestNewLeadTimeModalRef.current.show()
          }
          onReOpen={() => this.reOpenQuoteConfirmationModalRef.current.show()}
          onCopyQuote={() => this.copyQuoteModalRef.current.copy()}
          onUpdatePartNumbers={() =>
            this.setState({ showUpdatePartNumbersConfirmationModal: true })
          }
        />
        <QuoteCancelModal
          entityId={quote.id}
          onChanged={this.cancel}
          show={showCancelConfirmationModal}
          onHide={() => this.setState({ showCancelConfirmationModal: false })}
        />
        <QuoteExpireModal
          entityId={quote.id}
          onChanged={this.expire}
          show={showExpireConfirmationModal}
          onHide={() => this.setState({ showExpireConfirmationModal: false })}
        />
        <LeadTimeChangesConfirmationModal
          show={this.state.showLeadTimeChangesConfirmationModal}
          onShowUpdated={(showLeadTimeChangesConfirmationModal) =>
            this.setState({ showLeadTimeChangesConfirmationModal })
          }
          onConfirm={this.confirmLeadTimeChanges}
        />
        <ReOpenQuoteConfirmationModal
          ref={this.reOpenQuoteConfirmationModalRef}
          quoteId={this.state.quote.id}
          canBeReOpenedStatus={this.state.quote.statusInformation.canBeReOpened}
          onChanged={this.loadQuote}
          productsWithValidationErrors={this.state.quote.products.filter(
            (x) => x.hasValidationErrors
          )}
        />
        <ChangeBranchModal
          ref={this.changeBranchModalRef}
          quote={this.state.quote}
          onChanged={this.loadQuote}
        />
        {appConfig.isManagementApp && (
          <ChangePriceZoneModal
            ref={this.changePriceZoneModalRef}
            quote={this.state.quote}
            onChanged={this.loadQuote}
          />
        )}
        {appConfig.isManagementApp && (
          <ReSendCreateNotificationModal
            ref={this.reSendCreateNotificationModalRef}
            quote={this.state.quote}
            onSent={this.loadQuote}
          />
        )}
        <CustomActivityModal
          ref={this.customActivityModalRef}
          quoteId={this.state.quote.id}
          onChanged={this.loadQuote}
        />
        <RequestNewLeadTimeModal
          ref={this.requestNewLeadTimeModalRef}
          quoteId={this.state.quote.id}
          onChanged={this.loadQuote}
        />
        <CopyQuoteModal
          ref={this.copyQuoteModalRef}
          quote={this.state.quote}
          quoteHasCustomLineItems={this.state.quote.customLineItems.length > 0}
          quoteHasOverriddenLeadTimes={this.state.quote.products.some(
            (x) =>
              x.leadTime.type === quoteProductLeadTimeType.buildToOrderCustom
          )}
          onSaved={this.navigateToQuote}
        />
        <CancelConfirmationModal
          title={`Quote - ${quote.name}`}
          text={`Are you sure you want to update the part numbers?`}
          show={showUpdatePartNumbersConfirmationModal}
          onShowUpdated={() => {}}
          onYes={() => this.handleOnUpdatePartNumbers()}
          onNo={() =>
            this.setState({ showUpdatePartNumbersConfirmationModal: false })
          }
          submitting={saving}
        />
      </div>
    );
  }
}

export default withRouter(QuoteViewPage);
